<template>
    <div>
        <form v-loading="loading" @submit.prevent="save">
            <div class="card max-w-md mx-auto">
                <h4 class="card__title">{{ title }}</h4>

                <agreement-texts
                    v-if="product && product.agreement_texts"
                    :product="product"
                />

                <p>
                    Skicka faktura när leveransstatus för adresser är:
                </p>

                <select-input
                    v-model="action"
                    :options="statuses"
                    :opt-out-sorting="true"
                    class="mb-0"
                />

                <h2 class="mt-lg text-md">Lägg till Fortnox-artiklar</h2>

                <div class="overflow-x-auto mt-sm">
                    <table class="table striped">
                        <thead>
                            <tr>
                                <th class="w-1/5">Art.nr</th>
                                <th>Namn</th>
                                <th class="w-1/5">Pris (exkl. moms)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(article, index) in articles">
                                <td>
                                    <checkbox-input
                                        v-model="article.is_checked"
                                    >
                                        {{ article.fortnox_article_id }}
                                    </checkbox-input>
                                </td>
                                <td>{{ article.name }}</td>
                                <td>
                                    <text-input
                                        v-model="article.price"
                                        name="price"
                                        type="text"
                                        :error="getArticleErrorMessage(index, 'price')"
                                    />
                                </td>
                                <td>
                                    <font-awesome-icon
                                        v-show="article.is_housework"
                                        icon="home"
                                        class="mr-md"
                                        :class="article === mostExpensiveArticle ? 'text-success' : ''"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <input-error
                    :errors="errors"
                    name="articles"
                />

                <h2 v-show="canUseTaxReduction" class="mt-lg text-md">Typ av arbete</h2>
                <div class="flex my-md">
                    <div v-show="canUseTaxReduction" class="mr-5xl">
                        <radio-input
                            v-model="houseworkType"
                            class="mb-md"
                            value="rot"
                        >
                            ROT
                        </radio-input>

                        <radio-input
                            v-model="houseworkType"
                            class="mb-lg"
                            value="rut"
                        >
                            RUT
                        </radio-input>
                    </div>
                    <div>
                        <div v-if="canUseTaxReduction" class="bg-neutral-lightest p-lg">
                            Skattereduktionen beräknas på artikeln <strong>{{ mostExpensiveArticle.name }}</strong> och blir totalt
                            <strong>{{ taxReduction }} kr</strong>.
                            <font-awesome-icon
                                v-tooltip.right="helpText"
                                icon="info-circle"
                                class="text-utility mr-sm"
                            />
                        </div>

                        <div v-else class="bg-neutral-lightest p-lg">
                            Det finns ingen artikel i fakturatillfället som är ROT eller RUT-berättigad.
                        </div>
                    </div>
                </div>

                <div class="overflow-x-auto">
                    <table class="table description-list not-striped">
                        <tbody>
                            <tr>
                                <td>Totalt pris (exkl. moms)</td>
                                <td>{{ total }} kr</td>
                            </tr>
                            <tr>
                                <td>Moms 25 %</td>
                                <td>{{ VAT }} kr</td>
                            </tr>
                            <tr>
                                <td>Skattereduktion</td>
                                <td>-{{ taxReduction }} kr</td>
                            </tr>
                            <tr>
                                <td>Kund ska betala (inkl. moms)</td>
                                <td>{{ totalToPay }} kr</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="flex max-w-md mx-auto justify-between">
                <a
                    class="btn is-ghost btn-lg"
                    :href="backUrl"
                >
                    Avbryt
                </a>

                <button
                    class="btn is-primary btn-lg w-full md:w-auto"
                    type="submit"
                >
                    Spara
                </button>
            </div>
        </form>
    </div>
</template>

<script>
    import AgreementTexts from './AgreementTexts';
    export default {
        components: {
            AgreementTexts,
        },
        props: {
            title: {
                type: String,
                required: true,
            },
            product: {
                type: Object,
                required: true,
            },
            invoiceTrigger: {
                type: Object,
                required: false,
                default: null,
            },
            initialArticles: {
                type: Array,
                required: true,
            },
        },

        data() {
            return {
                houseworkType: 'rot',
                action: 'dig',
                statuses: {
                    dig: 'Grävd',
                    installation: 'Installerad',
                    activation: 'Redo för driftsättning',
                    activated: 'Driftsatt',
                    operational: 'I drift',
                },
                provider: 'fortnox',
                articles: this.initialArticles,
                errors: {},
                loading: false,
            };
        },

        computed: {
            requestRoute() {
                return this.invoiceTrigger
                    ? route('api.products.invoice-triggers.update', [this.product, this.invoiceTrigger])
                    : route('api.products.invoice-triggers.store', this.product);
            },
            requestMethod() {
                return this.invoiceTrigger ? 'patch' : 'post';
            },
            checkedArticles() {
                return this.articles
                    .filter(article => article.is_checked)
                    .map(article => {
                        return {
                            fortnox_article_id: article.fortnox_article_id,
                            price: article.price,
                        };
                    });
            },
            mostExpensiveArticle() {
                return this.articles.reduce((max, article) => {
                    return (article.is_checked && article.is_housework) && article.price >= max.price ? article : max;
                }, { price: null });
            },
            canUseTaxReduction() {
                return this.mostExpensiveArticle.price !== null;
            },
            taxReduction() {
                const articlePrice = this.mostExpensiveArticle.price * 1.25;

                return this.houseworkType === 'rot' ?
                    Math.round(articlePrice * 0.3) : Math.round(articlePrice * 0.5);
            },
            helpText() {
                return `${this.houseworkType.toUpperCase()} beräknas på artikeln som innebär husarbete
                (inkl. moms) * ${this.houseworkType === 'rot' ? '0,3' : '0,5'}. Finns det flera artiklar med husarbete
                för detta fakturatillfälle beräknas ${this.houseworkType.toUpperCase()} på den dyraste artikeln av dessa.`;
            },
            total() {
                return Math.round(this.checkedArticles.reduce((a, b) => +a + +b.price, 0));
            },
            VAT() {
                return Math.round(this.total * 0.25);
            },
            totalToPay() {
                return Math.round((this.total + this.VAT) - this.taxReduction);
            },
            backUrl() {
                return route('admin.products.show', this.product);
            },
        },

        created() {
            if (this.invoiceTrigger) {
                this.houseworkType = this.invoiceTrigger.housework_type;
                this.action = this.invoiceTrigger.action;
            }
        },

        methods: {
            async save() {
                this.loading = true;

                try {
                    await axios({
                        method: this.requestMethod,
                        url: this.requestRoute,
                        data: {
                            action: this.action,
                            articles: this.checkedArticles,
                            housework_type: this.houseworkType,
                            provider: 'fortnox',
                        },
                    });
                    window.location.href = this.backUrl;
                } catch (error) {
                    if (error.response.status === 422) {
                        flash_error('Formuläret är inte korrekt ifyllt');
                        this.errors = error.response.data.errors;
                    } else {
                        flash_error('Faktureringstillfället kunde inte sparas.');
                    }

                    this.loading = false;
                }
            },

            getArticleErrorMessage(index, key) {
                if (this.errors && this.errors[`articles.${index}.${key}`]) {
                    return this.errors[`articles.${index}.${key}`][0];
                }
            },
        },
    };
</script>
